import ConsultationRichInput from '@brands/Components/Inputs/RichTextField/RichTextField';
import { useWindowSize } from '@brands/hooks';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import React, { useMemo } from 'react';
import Modal, { Styles } from 'react-modal';

import styles from './styles.module.scss';

type ReportTextModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (value: string) => void;
  value: string;
  setValue: (value: string) => void;
  title: string;
  submitText: string;
  placeholder?: string;
  warningText?: string;
  isReadOnly?: boolean;
  isLoading?: boolean;
  saveDisabled?: boolean;
  maxHeight?: string;
};

const ReportTextModal = ({
  open,
  onClose,
  onSave,
  value,
  setValue,
  title,
  submitText,
  placeholder = 'Write here...',
  warningText,
  isReadOnly = false,
  isLoading = false,
  saveDisabled = false,
  maxHeight = '750px',
}: ReportTextModalProps): JSX.Element => {
  const screenSize = useWindowSize();
  const isMobile = useMemo(() => screenSize.width < 768, [screenSize.width]);

  const isHtmlEmpty = (html?: string): boolean => {
    if (!html) return true;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const text = (doc.body.textContent || '')
      .replace(/\u00A0/g, ' ')
      .replace(/\u200B/g, '')
      .trim();

    return text.length === 0;
  };

  const mobileStyles: Styles = {
    ...customStylesDashboard,
    overlay: {
      ...(customStylesDashboard.overlay || {}),
      overflowY: 'auto',
    },
    content: {
      ...(customStylesDashboard.content || {}),
      width: '100vw',
      height: '100vh',
      inset: '0',
      padding: '90px 16px 32px 16px',
      borderRadius: '0',
      maxHeight: '100vh',
    },
  };

  const desktopStyles: Styles = {
    ...customStylesDashboard,
    content: {
      ...(customStylesDashboard.content || {}),
      width: '444px',
      padding: '30px 32px 32px',
      maxHeight,
    },
  };

  const isSaveDisabled = isReadOnly || isLoading || saveDisabled || isHtmlEmpty(value);

  const handleSave = (): void => {
    if (isSaveDisabled) return;
    onSave(value);
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      ariaHideApp={false}
      shouldCloseOnOverlayClick={!isLoading}
      shouldCloseOnEsc={!isLoading}
      style={isMobile ? mobileStyles : desktopStyles}
    >
      <div className={styles.NoteModal}>
        <div className={styles.bodyHead}>
          <CloseIcon
            onClick={() => {
              if (isLoading) return;
              onClose();
            }}
            style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
          />
        </div>

        <div className={`${styles.body} ${styles.formContainer}`}>
          <span className={styles.bodyHeader}>{title}</span>

          <div className={styles.bodySub}>
            <ConsultationRichInput
              value={value || ''}
              editable={!isReadOnly}
              placeholder={placeholder}
              className={`fs-exclude ${styles.caseNotesInput}`}
              onChange={(html) => {
                setValue(html);
              }}
            />
          </div>
        </div>

        <div className={styles.bodyFooter}>
          {warningText && <span className={styles.internalNoteWarning}>{warningText}</span>}

          <Button
            variant="contained"
            className={styles.containedBtn}
            onClick={handleSave}
          >
            {isLoading ? 'Saving...' : submitText}
          </Button>

          <Button
            variant="text"
            className={styles.textBtn}
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReportTextModal;